// ─────────────────────────────────────────────
// Oike — Marketing › Contenido › Plan semanal (pure; tested by scripts/test-content-plan.mjs). Approved by Ale on
// 04-11-2026 (part A). Only the owner. The code decides how many contents, channels, days and steps; the AI only drafts
// texts (in the pieces of Mi marca, with its controls). Nothing here writes or publishes.
// - A content of the plan has a STABLE identity (itemKey) and one or more formats, each a piece of Mi marca. Its shared
//   fields (objective, topic, call to action) live in the plan; each format keeps only its own texts. A change of the
//   shared fields changes its fingerprint, and the formats reviewed with another fingerprint are «a revisar».
// - Steps: the common ones belong to the content (one «Preparar guion», one «Grabar», one «Editar» for a video that also
//   goes to Reels and Shorts); only what an adaptation really needs is its own step (export an image, publish in a
//   channel). Each step has a content key (contentKey) that never depends on a format's piece id, so a retry, a second
//   approval or a new format never duplicates a task nor reopens a completed one.
// - Capacity, channels and days are configured once and can be adjusted for a week. Estimates are suggested. The calendar
//   says how busy a day is, never how free: the plan picks among Ale's days the least busy one and never fills gaps.
// ─────────────────────────────────────────────

import { isPizarraTemplate, pizarraEdited, pizarraPreview, PLAN_PIZARRA } from './pizarraSocial.js';
export const CHANNELS = {
  tiktok: { label: 'TikTok', format: 'tiktok', template: 'tiktok', video: true },
  // Brand renewal (04-11-2026): new contents use the Pizarra templates. A content saved before keeps its format (and the
  // keys of its tasks); a new piece for it is a Pizarra piece too (PLAN_PIZARRA). Saved pieces are never migrated.
  linkedin: { label: 'LinkedIn', format: 'li_square', template: 'pz_li_post' },
  instagram: { label: 'Instagram', format: 'ig_carousel', template: 'pz_ig_carousel' },
};
export const VIDEO_PUBLISH = { tiktok: 'TikTok', reels: 'Instagram Reels', shorts: 'YouTube Shorts' };
export const OBJECTIVES = [
  { key: 'inquiries', label: 'Que me escriban: consultas' },
  { key: 'trust', label: 'Confianza de quien ya me sigue' },
  { key: 'explain', label: 'Explicar cómo funciona lo que ofrezco' },
  { key: 'reach', label: 'Que me conozca más gente del público' },
  { key: 'event', label: 'Inscripciones al evento' },
];
export const objectiveLabel = (k) => OBJECTIVES.find(o => o.key === k)?.label || k;
export const STEP_LABELS = { script: 'Preparar el guion', record: 'Grabar el video', edit: 'Editar el video', design: 'Revisar y exportar la imagen', publish: 'Publicar' };
export const DEFAULT_ESTIMATES = { script: 15, record: 20, edit: 30, design: 15, publish: 5 };
export const WEEKDAYS = ['', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];
export const DEFAULT_CONFIG = {
  capacityMinutes: 120, maxItems: 3, channels: ['tiktok', 'linkedin', 'instagram'], videoPublish: ['tiktok'],
  days: { prepare: [1, 2], record: [2, 4], publish: [2, 4, 5] }, estimates: DEFAULT_ESTIMATES,
  audiences: [], offers: [], ctaLinks: {}, materials: { items: [], tools: [] }, timeZone: 'Europe/Madrid', contactText: '',
};
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const KEY_RE = /^[a-z0-9-]{4,40}$/;
const clip = (v, n) => String(v ?? '').replace(/\s+/g, ' ').trim().slice(0, n);
const days = (list) => [...new Set((Array.isArray(list) ? list : []).map(Number).filter(d => Number.isInteger(d) && d >= 1 && d <= 7))].sort();
const isTz = (tz) => { try { if (typeof tz !== 'string' || !tz || tz.length > 64) return false; new Intl.DateTimeFormat('es', { timeZone: tz }); return true; } catch { return false; } };

/** A clean configuration (never more than 3 contents a week, never daily by default). */
export function configOf(v = {}) {
  const c = v && typeof v === 'object' ? v : {};
  const channels = (Array.isArray(c.channels) ? c.channels : DEFAULT_CONFIG.channels).filter(k => CHANNELS[k]);
  const est = { ...DEFAULT_ESTIMATES };
  for (const k of Object.keys(est)) { const n = Number(c.estimates?.[k]); if (Number.isFinite(n) && n >= 0 && n <= 480) est[k] = Math.round(n); }
  const keyed = (list, max, f) => (Array.isArray(list) ? list : []).map(f).filter(Boolean).slice(0, max);
  return {
    capacityMinutes: Math.min(1200, Math.max(15, Math.round(Number(c.capacityMinutes) || DEFAULT_CONFIG.capacityMinutes))),
    maxItems: Math.min(3, Math.max(1, Math.round(Number(c.maxItems) || DEFAULT_CONFIG.maxItems))),
    channels: channels.length ? [...new Set(channels)] : DEFAULT_CONFIG.channels,
    videoPublish: (() => { const l = [...new Set((Array.isArray(c.videoPublish) ? c.videoPublish : DEFAULT_CONFIG.videoPublish).filter(k => VIDEO_PUBLISH[k]))]; return l.length ? l : ['tiktok']; })(),
    days: { prepare: days(c.days?.prepare).length ? days(c.days.prepare) : DEFAULT_CONFIG.days.prepare, record: days(c.days?.record).length ? days(c.days.record) : DEFAULT_CONFIG.days.record, publish: days(c.days?.publish).length ? days(c.days.publish) : DEFAULT_CONFIG.days.publish },
    estimates: est,
    // Audiences and offers: Ale's own words, reused every week (never taken from contacts or clients)
    audiences: uniqueKeys(keyed(c.audiences, 12, a => (a && clip(a.label, 80) ? { key: typeof a.key === 'string' && KEY_RE.test(a.key) ? a.key : slug(a.label), label: clip(a.label, 80), description: clip(a.description, 400) } : null))),
    offers: uniqueKeys(keyed(c.offers, 12, o => (o && clip(o.label, 100) ? { key: typeof o.key === 'string' && KEY_RE.test(o.key) ? o.key : slug(o.label), label: clip(o.label, 100), description: clip(o.description, 800), topicCode: clip(o.topicCode, 40) } : null))),
    // Identified links of Solicitudes per channel and topic: «tiktok» or «tiktok:<topic>» → link id
    ctaLinks: Object.fromEntries(Object.entries(c.ctaLinks && typeof c.ctaLinks === 'object' ? c.ctaLinks : {}).filter(([k, id]) => /^[a-z]+(:[a-z0-9-]{1,40})?$/.test(k) && /^rec[A-Za-z0-9]{14}$/.test(String(id))).slice(0, 40)),
    materials: { items: (Array.isArray(c.materials?.items) ? c.materials.items : []).filter(x => x && /^rec[A-Za-z0-9]{14}$/.test(x.id)).slice(0, 6).map(x => ({ id: x.id, notes: x.notes === true, fragments: (Array.isArray(x.fragments) ? x.fragments : []).filter(f => /^rec[A-Za-z0-9]{14}$/.test(f)).slice(0, 12) })), tools: (Array.isArray(c.materials?.tools) ? c.materials.tools : []).filter(t => /^rec[A-Za-z0-9]{14}$/.test(t)).slice(0, 4) },
    timeZone: isTz(c.timeZone) ? c.timeZone : DEFAULT_CONFIG.timeZone,
    // How people contact her when a channel has no identified link (her words, asked once and reused)
    contactText: clip(c.contactText, 200),
  };
}
/** Each audience or offer keeps a stable key; two with the same name get a suffix (never two with one key). */
function uniqueKeys(list) { const seen = new Set(); return list.map(x => { let k = x.key; let n = 2; while (seen.has(k)) k = `${x.key.slice(0, 36)}-${n++}`; seen.add(k); return { ...x, key: k }; }); }
export function slug(s) { return String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'x'; }

/** Monday of the week of a day (YYYY-MM-DD). */
export function weekOf(day) {
  const d = new Date(`${day}T12:00:00Z`);
  const wd = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() - (wd - 1));
  return d.toISOString().slice(0, 10);
}
const addDays = (day, n) => { const d = new Date(`${day}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
export const dayOfWeek = (week, wd) => addDays(week, wd - 1);

/** A short deterministic fingerprint (FNV-1a) of the shared fields of a content. */
export function sharedFingerprint(item) {
  const parts = JSON.stringify([item.objective || '', clip(item.topic, 300), item.cta?.linkId || '', clip(item.cta?.text, 200)]);
  let h = 0x811c9dc5;
  for (const ch of parts) { h ^= ch.charCodeAt(0); h = Math.imul(h, 0x01000193) >>> 0; }
  return h.toString(36);
}

/**
 * The formats of a content: its main channel and its adaptations to other channels (each a piece of Mi marca, sharing
 * the content's topic and call to action). A video published in Reels or Shorts is the same piece, not a new format.
 */
export function formatsOf(item) {
  const chans = [item.channel, ...(Array.isArray(item.adaptations) ? item.adaptations : [])].filter((c, i, a) => CHANNELS[c] && a.indexOf(c) === i);
  // A channel the content already had keeps its saved format (never rewritten by a later default)
  const saved = (c) => (Array.isArray(item.formats) ? item.formats : []).find(f => f && f.channel === c && typeof f.format === 'string');
  return chans.map(c => { const s = saved(c); return { format: s ? s.format : CHANNELS[c].format, template: s?.template || CHANNELS[c].template, channel: c, main: c === item.channel }; });
}
/** The template of a new piece of a plan's format: a Pizarra template for Instagram and LinkedIn, TikTok's script. */
export const planTemplateOf = (fmt) => PLAN_PIZARRA[fmt?.format] || fmt?.template;
/** Where a content is published: a video in each chosen video channel; an image in its channel. */
export function publishChannelsOf(item, config) {
  return CHANNELS[item.channel]?.video ? (item.publishChannels?.length ? item.publishChannels : config.videoPublish).filter(k => VIDEO_PUBLISH[k]) : [item.channel];
}
const channelName = (k) => VIDEO_PUBLISH[k] || CHANNELS[k]?.label || k;

/**
 * The steps of a content with their content keys. Common steps belong to the content (itemKey); only an adaptation
 * that needs work has its own (an image to export, a publication per channel).
 */
export function stepsOf(item, config) {
  const est = config.estimates || DEFAULT_ESTIMATES;
  const out = [];
  if (CHANNELS[item.channel]?.video) {
    for (const s of ['script', 'record', 'edit']) out.push({ key: `${item.key}:${s}`, step: s, label: STEP_LABELS[s], minutes: est[s], shared: true, phase: s === 'script' ? 'prepare' : 'record' });
  } else {
    const mainFormat = formatsOf(item).find(f => f.main)?.format || CHANNELS[item.channel]?.format;
    out.push({ key: `${item.key}:${mainFormat}:design`, step: 'design', label: STEP_LABELS.design, minutes: est.design, shared: false, format: mainFormat, phase: 'prepare' });
  }
  for (const ch of publishChannelsOf(item, config)) out.push({ key: `${item.key}:publish:${ch}`, step: 'publish', channel: ch, label: `${STEP_LABELS.publish} en ${channelName(ch)}`, minutes: est.publish, shared: false, phase: 'publish' });
  // Adaptations to an image channel: only their own work (export and publish); the topic and the recording are shared
  for (const f of formatsOf(item).filter(x => !x.main)) {
    if (CHANNELS[f.channel].video) continue;
    if (!out.some(x => x.key === `${item.key}:${f.format}:design`)) out.push({ key: `${item.key}:${f.format}:design`, step: 'design', label: `${STEP_LABELS.design} (${CHANNELS[f.channel].label})`, minutes: est.design, shared: false, format: f.format, phase: 'prepare' });
    if (!out.some(x => x.key === `${item.key}:publish:${f.channel}`)) out.push({ key: `${item.key}:publish:${f.channel}`, step: 'publish', channel: f.channel, label: `${STEP_LABELS.publish} en ${CHANNELS[f.channel].label}`, minutes: est.publish, shared: false, phase: 'publish' });
  }
  return out;
}

/**
 * Picks a day among the allowed weekdays: the least busy one (tasks and calendar events), not after `latest`.
 * occupation: { 'YYYY-MM-DD': { tasks, minutes, events } }. Busy is a count, never «free time».
 */
function pickDay(week, allowed, occupation, { latest = null, earliest = null, used = {}, today = null } = {}) {
  // Never a day that already passed: in the current week, only from today on
  const floor = today && today > week ? today : null;
  const all = allowed.map(wd => dayOfWeek(week, wd));
  const future = floor ? all.filter(d => d >= floor) : all;
  const cand = future.filter(d => (!latest || d <= latest) && (!earliest || d >= earliest));
  const list = cand.length ? cand : future.length ? future : floor && floor <= addDays(week, 6) ? [floor] : all;
  const load = (d) => (occupation[d]?.tasks || 0) * 2 + (occupation[d]?.events || 0) + (used[d] || 0) * 3;
  return list.slice().sort((a, b) => load(a) - load(b) || a.localeCompare(b))[0];
}

/**
 * The plan of a week by rules: how many contents fit the capacity (1 to maxItems; TikTok first when chosen), their
 * channel and objective, and a suggested day for each step. The AI never decides this.
 */
export function planWeek({ week, config, occupation = {}, focus = {}, today = null, keyOf = (i) => `c${week.replace(/-/g, '')}-${i + 1}` }) {
  const cfg = configOf(config);
  const channels = cfg.channels.includes('tiktok') ? ['tiktok', ...cfg.channels.filter(c => c !== 'tiktok')] : cfg.channels;
  const objectives = focus.kind === 'event' ? ['event', 'trust', 'explain'] : ['inquiries', 'trust', 'explain'];
  const items = []; let total = 0; const used = {};
  for (let i = 0; i < cfg.maxItems; i++) {
    const item = { key: keyOf(i), channel: channels[i % channels.length], objective: objectives[i] || 'reach', topic: '', cta: null, publishChannels: CHANNELS[channels[i % channels.length]].video ? cfg.videoPublish : [] };
    const steps = stepsOf(item, cfg);
    const minutes = steps.reduce((s, x) => s + x.minutes, 0);
    if (items.length && total + minutes > cfg.capacityMinutes) break;
    // Days: publish on a least busy allowed day; record and prepare on or before it
    const publishDay = pickDay(week, cfg.days.publish, occupation, { used, today });
    used[publishDay] = (used[publishDay] || 0) + 1;
    const recordDay = pickDay(week, cfg.days.record, occupation, { latest: publishDay, used, today });
    const prepareDay = pickDay(week, cfg.days.prepare, occupation, { latest: recordDay <= publishDay ? recordDay : publishDay, used, today });
    item.steps = steps.map(s => ({ ...s, dueOn: s.phase === 'publish' ? publishDay : s.phase === 'record' ? recordDay : prepareDay, include: true }));
    item.minutes = minutes;
    total += minutes;
    items.push(item);
  }
  return { week, items, minutes: total, capacity: cfg.capacityMinutes, overCapacity: total > cfg.capacityMinutes, busy: Object.fromEntries(Object.entries(occupation).filter(([d]) => d >= week && d <= addDays(week, 6))) };
}

/**
 * Merges the steps of a content after a change (a format added, an edit) keeping what Ale adjusted: her dates, the steps
 * she removed, and the task already linked to each content key.
 */
export function mergeSteps(previous = [], next = []) {
  const byKey = new Map(previous.map(s => [s.key, s]));
  return next.map(s => {
    const p = byKey.get(s.key);
    return p ? { ...s, dueOn: p.dueOn || s.dueOn, include: p.include !== false, edited: !!p.edited, minutes: p.edited && Number.isFinite(p.minutes) ? p.minutes : s.minutes, taskId: p.taskId || null } : { ...s, include: true };
  });
}

/** Whether each format of a content needs review: reviewed with another fingerprint of the shared fields. */
export function reviewOf(item, pieces = []) {
  const fp = sharedFingerprint(item);
  return (item.formats || []).map(f => {
    const p = pieces.find(x => x.id === f.pieceId);
    return { ...f, exists: !!p, status: p?.status || null, review: !!p && (p.planFp || '') !== fp };
  });
}

/**
 * The tasks an approval would create, and the ones it reuses. A content key that already has a task (open or done)
 * is reused and never recreated nor reopened. Discarded contents and removed steps create nothing.
 */
export function taskPlan(plan, existing = []) {
  const byKey = new Map();
  for (const t of existing) if (t.source?.kind === 'content' && t.source.contentKey && !byKey.has(t.source.contentKey)) byKey.set(t.source.contentKey, t);
  const create = []; const reuse = []; const skipped = [];
  for (const item of plan.items || []) {
    if (item.discarded) continue;
    for (const s of item.steps || []) {
      if (s.include === false) { skipped.push(s.key); continue; }
      const t = byKey.get(s.key);
      if (t) { reuse.push({ key: s.key, taskId: t.id, status: t.status, itemKey: item.key }); continue; }
      const mainPiece = (item.formats || [])[0]?.pieceId || null;
      create.push({
        key: s.key, itemKey: item.key, title: `${s.label} · ${clip(item.topic, 70) || 'contenido de la semana'}`, dueOn: DAY_RE.test(s.dueOn || '') ? s.dueOn : null, minutes: s.minutes,
        source: { kind: 'content', week: plan.week, itemKey: item.key, step: s.step, contentKey: s.key, ...(s.channel ? { channel: s.channel } : {}), ...(mainPiece ? { pieceId: mainPiece } : {}) },
      });
    }
  }
  return { create, reuse, skipped, minutes: create.reduce((n, x) => n + (x.minutes || 0), 0) };
}

/** The total time of the steps that would be done (shown as suggested, never as a commitment). */
export const planMinutes = (plan) => (plan.items || []).filter(i => !i.discarded).reduce((n, i) => n + (i.steps || []).filter(s => s.include !== false).reduce((m, s) => m + (s.minutes || 0), 0), 0);

/** The brief that goes to the AI with the authorized materials: only Ale's words (offer, audience, objective, topic, CTA). */
export function briefOf(plan, item, { channelLabel } = {}) {
  return [
    item.brief ? `Lo que quiere comunicar: ${clip(item.brief, 300)}` : `Promover: ${clip(plan.focus?.label, 120)}${plan.focus?.description ? ` — ${clip(plan.focus.description, 600)}` : ''}`,
    (item.audience || plan.audience)?.label && `Público: ${clip((item.audience || plan.audience).label, 80)}${(item.audience || plan.audience).description ? ` — ${clip((item.audience || plan.audience).description, 300)}` : ''}`,
    `Canal: ${channelLabel || CHANNELS[item.channel]?.label || item.channel}`,
    `Objetivo: ${objectiveLabel(item.objective)}`,
    item.topic && `Tema: ${clip(item.topic, 300)}`,
    item.cta?.text && `Llamada a la acción: ${clip(item.cta.text, 200)}${CHANNELS[item.channel]?.video ? ' (en TikTok: «el enlace está en mi perfil»)' : ''}`,
  ].filter(Boolean).join('\n').slice(0, 1400);
}


// ── Complete proposal (adjusted with Ale on 04-11-2026): «Preparar plan» leaves topics, objective and call to action per
// content and the drafts of every chosen format, for her to review. Nothing is created as a task until she approves. ──

/**
 * What is indispensable before the AI writes anything, asked once (the answers are kept in the configuration):
 * - an offer without its description (the AI would have to invent what it includes);
 * - a content that asks for a reply (inquiries or event) in a channel without an identified link, when she never said
 *   how people should contact her.
 * Blockers cannot be answered here: a missing brand profile is created in Mi marca.
 */
export function missingInfo({ focus = {}, items = [], config = {}, brandReady = true }) {
  const questions = []; const blockers = [];
  if (!brandReady) blockers.push({ key: 'brand_profile', label: 'Falta tu perfil de marca: crealo en Mi marca (una vez).' });
  if (focus.kind === 'offer' && !clip(focus.description, 800)) {
    questions.push({ key: 'offerDescription', label: `¿Qué es y qué incluye «${clip(focus.label, 100)}»? Con tus palabras: la IA no agrega nada que no esté aquí (ni plazas, ni precios, ni resultados).`, max: 800 });
  }
  const noLink = [...new Set(items.filter(i => ['inquiries', 'event'].includes(i.objective) && !i.cta?.linkId).map(i => CHANNELS[i.channel]?.label || i.channel))];
  if (noLink.length && !clip(config.contactText, 200)) {
    questions.push({ key: 'contactText', label: `${noLink.join(' y ')} no tiene enlace identificado. ¿Cómo querés que te contacten? (ej.: «escribime por mensaje privado»). Se guarda para las próximas semanas.`, max: 200 });
  }
  return { questions, blockers };
}

/** What a complete preparation will draft: contents, formats and the AI calls (one for topics, one per format). */
export function scopeOf(items = []) {
  const live = items.filter(i => !i.discarded);
  const formats = live.flatMap(i => formatsOf(i).map(f => ({ itemKey: i.key, channel: f.channel, format: f.format, main: f.main })));
  return { contents: live.length, formats: formats.length, list: formats, aiCalls: formats.length + 1 };
}

/**
 * The drafts of a plan: done (a piece exists), skipped (she discarded that draft or chose to write it herself later) and
 * pending. A failure is kept by format, so «Continuar» drafts only what is missing and never repeats a completed one.
 */
export function generationOf(plan) {
  const list = [];
  for (const it of plan?.items || []) {
    if (it.discarded) continue;
    for (const f of it.formats || []) list.push({ itemKey: it.key, format: f.format, channel: f.channel, done: !!f.pieceId, skipped: !f.pieceId && !!f.skip, error: f.pieceId ? null : plan.generation?.failed?.[`${it.key}|${f.format}`] || null });
  }
  const pending = list.filter(x => !x.done && !x.skipped);
  return { list, pending, done: list.filter(x => x.done).length, failed: pending.filter(x => x.error).length, started: !!plan?.generation, complete: !!plan?.generation && !pending.length };
}

// Claims the AI must never add on its own: places or availability, prices, discounts or free offers. Allowed only when
// they are in Ale's words (offer, focus, contact text) or in the materials she authorized.
const CLAIM_RE = /(plazas?|cupos?|vacantes|lugares (?:disponibles|limitados)|[úu]ltimos lugares|quedan \d+|precios?|descuentos?|promoci[oó]n|gratis|gratuit[oa]s?|sin costo|\d+ ?(?:€|euros?|usd|d[oó]lares)|\$ ?\d+)/gi;
const norm = (v) => String(v || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
export function claimsOf(text, allowed = '') {
  const a = norm(allowed);
  return [...new Set((String(text || '').match(CLAIM_RE) || []).map(x => x.toLowerCase()))].filter(x => !a.includes(norm(x)));
}

/** A proposed topic and call to action, checked by the code: no invented claims, no link where there is none. */
export function proposalFor(item, raw, { allowed = '', fallbackCta = '' } = {}) {
  const topic = clip(raw?.topic, 120);
  let cta = clip(raw?.cta, 200);
  const flagged = [];
  if (topic && claimsOf(topic, allowed).length) flagged.push('topic');
  if (cta && (claimsOf(cta, allowed).length || (!item.cta?.linkId && /enlace|link|https?:/i.test(cta)) || /https?:\/\//i.test(cta))) { flagged.push('cta'); cta = ''; }
  return { topic: flagged.includes('topic') ? '' : topic, cta: cta || fallbackCta, flagged };
}

/** Whether Ale already touched a piece: an edited or her own text, a reviewed caption, or an approval. */
export function pieceEdited(piece) {
  if (!piece) return false;
  if (isPizarraTemplate(piece.template)) return pizarraEdited(piece);
  if (piece.status === 'approved') return true;
  const c = piece.content || {};
  const slots = [...Object.values(c.slots || {}), ...(c.slides || []).flatMap(sl => Object.values(sl.slots || {}))];
  if (slots.some(x => x && String(x.text || '').trim() && ['mine', 'ai_edited', 'ai_reviewed'].includes(x.origin) && x !== c.slots?.signature)) return true;
  return ['ai_edited', 'ai_reviewed'].includes(c.captionOrigin) || (c.captionOrigin === 'mine' && !!String(piece.caption || '').trim());
}

/** A short text preview of a piece (to review the proposal without opening each editor). */
export function piecePreview(piece, labels = {}) {
  if (!piece?.content) return [];
  if (isPizarraTemplate(piece.template)) return pizarraPreview(piece);
  const c = piece.content; const out = [];
  for (const [k, v] of Object.entries(c.slots || {})) if (k !== 'signature' && String(v?.text || '').trim()) out.push({ key: k, label: labels[k] || k, text: String(v.text).slice(0, 900) });
  (c.slides || []).forEach((sl, i) => { const t = Object.values(sl.slots || {}).map(v => String(v?.text || '').trim()).filter(Boolean).join(' · '); if (t) out.push({ key: `slide${i}`, label: `Lámina ${i + 1}`, text: t.slice(0, 600) }); });
  if (String(piece.caption || '').trim()) out.push({ key: 'caption', label: 'Texto que acompaña', text: String(piece.caption).slice(0, 900) });
  return out;
}

// ── Ficha de un contenido en uso (agreed with Ale on 04-11-2026): script, recording and publications. The facts she
// registers here (recorded, the video file link, each publication link) live in the content (owner_settings, only
// references); the linked tasks are the same Personal Tasks of Hoy, found only by their content key. A task completed
// in Hoy counts as that fact; registering it here completes that task. Preparing a script is never recording it. ──

export const PUBLICATION_HOSTS = {
  tiktok: /(^|\.)tiktok\.com$/, reels: /(^|\.)instagram\.com$/, shorts: /(^|\.)(youtube\.com|youtu\.be)$/,
  linkedin: /(^|\.)linkedin\.com$/, instagram: /(^|\.)instagram\.com$/,
};
const urlOf = (v) => { try { const u = new URL(String(v || '').trim()); return u.protocol === 'https:' ? u : null; } catch { return null; } };
/** The public link of a publication in that channel (its own domain), or null. */
export function publicationUrlOk(channel, url) { const u = urlOf(url); return u && PUBLICATION_HOSTS[channel]?.test(u.hostname.toLowerCase()) ? u.toString() : null; }
/** The link to her recorded file (any https link: Drive, Dropbox…): never a publication. */
export function fileUrlOk(url) { const u = urlOf(url); return u ? u.toString() : null; }
export const channelLabel = (k) => VIDEO_PUBLISH[k] || CHANNELS[k]?.label || k;

/** The task of a step of this content, only if exactly one has its key (unequivocal); several are «ambiguous». */
export function linkedTask(tasks, week, itemKey, contentKey) {
  const list = (tasks || []).filter(t => t.source?.kind === 'content' && t.source.contentKey === contentKey && t.source.itemKey === itemKey && (!t.source.week || t.source.week === week));
  return list.length === 1 ? { task: list[0], ambiguous: false } : { task: null, ambiguous: list.length > 1 };
}

/** What is done, from her facts and the linked tasks: «por grabar», «grabado», «publicado», and the channels missing. */
export function progressOf(item, tasks, config, week, facts = null) {
  // Her facts live in the content's piece (they last as long as the piece); an older content may still carry them
  const p = facts || item.progress || {};
  const video = !!CHANNELS[item.channel]?.video;
  const rec = linkedTask(tasks, week, item.key, `${item.key}:record`);
  const recorded = video ? (p.recorded ? { at: p.recorded.at, via: 'ficha' } : rec.task?.status === 'done' ? { at: rec.task.doneOn || null, via: 'hoy' } : null) : null;
  const chosen = publishChannelsOf(item, configOf(config || {}));
  const extra = Object.keys(p.published || {}).filter(c => !chosen.includes(c));
  const channels = [...chosen, ...extra].map(c => {
    const pub = p.published?.[c] || null;
    const lt = linkedTask(tasks, week, item.key, `${item.key}:publish:${c}`);
    return { channel: c, label: channelLabel(c), chosen: chosen.includes(c), url: pub?.url || '', at: pub?.at || null, published: !!pub || lt.task?.status === 'done', viaHoy: !pub && lt.task?.status === 'done', task: lt.task ? { id: lt.task.id, status: lt.task.status } : null, ambiguous: lt.ambiguous };
  });
  const missing = channels.filter(c => c.chosen && !c.published).map(c => c.label);
  const any = channels.some(c => c.published);
  const state = any ? (missing.length ? 'published_partial' : 'published') : recorded ? 'recorded' : video ? 'to_record' : 'to_publish';
  return { video, recorded, recordTask: rec.task ? { id: rec.task.id, status: rec.task.status } : null, recordAmbiguous: rec.ambiguous, videoUrl: p.videoUrl || '', channels, missing, state };
}
export const PROGRESS_LABEL = { to_record: 'Por grabar', recorded: 'Grabado', published: 'Publicado', published_partial: 'Publicado en parte', to_publish: 'Por publicar' };
